// The Affärskvitto — Klargo's own summary of a completed deal, as the paper it opens as.
//
// A port of the Lovable design's buildReceiptPreviewDocument (components/ds/retainedDocuments.ts)
// and its event resolver (transactionEvents.ts), on the real backend. The paper is built from
// blocks (title, meta, fields, clause, signoff) that PaperDocumentView sets, so the receipt and the
// reference cannot drift.
//
// TRUTH BOUNDARY, the rule for every paper built from these blocks:
//   1. A VALUE renders only when the deal actually holds it.
//   2. No fact is derived from a different fact's date or state: an event line speaks from that
//      event's OWN timestamp or not at all, so a deal whose payment confirmation carries no date
//      gets no payment line — the receipt never restates completion as payment evidence.
//   3. Absence of data produces a shorter document, never an inferred one.
//
// WHERE THE VALUES COME FROM. The vehicle, the price, the parties and each event's date come from
// the deal (the signed contract, the payment and ownership rows of its log). The reference is
// derived from the contract's id the way the reference derives it — there is no stored number — and
// the explanatory copy is constant text.
import { formatDateLocalized, formatRegNumber, formatSekAmount } from "./format";

/** KLG-2026-000123 — a stable, readable number derived from the contract's id. */
export const contractNumber = (contractId, isoDate) => {
  const digits = String(contractId ?? "").replace(/\D/g, "").padStart(6, "0").slice(-6);
  const year = (isoDate || "").slice(0, 4) || String(new Date().getFullYear());
  return `KLG-${year}-${digits}`;
};

/** One deal, one reference: the receipt, and anything else that states it, derive the same one. */
export const dealReference = (contractId, signedAt) => contractNumber(contractId, signedAt);

// Same-day events keep the flow order, because the log holds a moment and no time is invented to
// separate two that carry the same one.
const FLOW_ORDER = { signed: 0, paid: 1, transferred: 2 };
const EVENT_LINE = {
  signed: "record.receipt.event.signed",
  paid: "record.receipt.event.paid",
  transferred: "record.receipt.event.transferred",
};

/** The confirmed events of the deal that HAVE their own timestamp, oldest first. */
export const resolveReceiptEvents = ({ signedAt, paidAt, transferredAt }) => {
  const events = [];
  if (signedAt) events.push({ kind: "signed", date: signedAt });
  if (paidAt) events.push({ kind: "paid", date: paidAt });
  if (transferredAt) events.push({ kind: "transferred", date: transferredAt });
  return events.sort((a, b) => String(a.date).localeCompare(String(b.date)) || FLOW_ORDER[a.kind] - FLOW_ORDER[b.kind]);
};

const fill = (template, values) =>
  Object.entries(values).reduce((text, [key, value]) => text.replace(`{${key}}`, value), template);

/**
 * @param {object} input
 * @param {number|string} input.contractId
 * @param {string} input.registrationNo  plate, as the deal holds it
 * @param {string} input.vehicleName     the vehicle as the transaction record names it
 * @param {number} input.amount          the agreed price
 * @param {string} input.sellerName
 * @param {string} input.buyerName
 * @param {string} [input.signedAt]      when the second party signed
 * @param {string} [input.paidAt]        when the seller confirmed the payment
 * @param {string} [input.transferredAt] when the seller reported the change of ownership
 * @param {(key: string) => string} input.tp  the shared dictionary
 * @param {string} input.language
 * @returns {{ brandLine: string, referenceLine: string, footerLine?: string, pages: object[] }}
 */
export const buildReceiptDocument = ({
  contractId, registrationNo, vehicleName, amount, sellerName, buyerName,
  signedAt, paidAt, transferredAt, tp, language,
}) => {
  const date = (iso) => formatDateLocalized(iso, language);
  const reference = dealReference(contractId, signedAt || transferredAt);
  // The receipt is created when the deal completes: the seller's statement is the last event.
  const createdAt = transferredAt;

  const metaFields = [
    { label: tp("record.receipt.reference"), value: reference },
    createdAt ? { label: tp("record.receipt.created"), value: date(createdAt) } : null,
    transferredAt ? { label: tp("record.receipt.completed"), value: date(transferredAt) } : null,
  ].filter(Boolean);

  const vehicleLine = [formatRegNumber(registrationNo), vehicleName].filter(Boolean).join(" · ");
  const dealFields = [
    { label: tp("record.receipt.vehicle"), value: vehicleLine },
    { label: tp("record.receipt.price"), value: formatSekAmount(amount, language) },
    { label: tp("record.receipt.seller"), value: sellerName },
    { label: tp("record.receipt.buyer"), value: buyerName },
  ].filter((field) => field.value);

  const events = resolveReceiptEvents({ signedAt, paidAt, transferredAt })
    .map((event) => fill(tp(EVENT_LINE[event.kind]), { date: date(event.date) }));

  const blocks = [
    { kind: "title", title: tp("ds.document.receipt"), subtitle: vehicleLine },
    { kind: "meta", fields: metaFields },
    { kind: "fields", heading: tp("record.receipt.deal"), fields: dealFields },
  ];
  if (events.length > 0) blocks.push({ kind: "clause", heading: tp("record.receipt.events"), paragraphs: events });
  blocks.push({
    kind: "signoff",
    heading: tp("record.receipt.issued"),
    paragraphs: [tp("record.receipt.issued.body")],
    company: tp("contract.preview.company"),
    footnote: tp("record.receipt.footnote"),
  });

  return {
    brandLine: tp("record.receipt.brandLine"),
    referenceLine: `${reference} · ${date(createdAt || signedAt)}`,
    pages: [{ blocks }],
  };
};
